// hops — same-moment policy hops (Moment ADR-040 D4).
//
// A hop is a triggered-by edge whose two ends are both nodes of the moment the
// edge belongs to: the canonical Event Storming column (event, the policy that
// reacts, the command it issues) in ONE column. Moment emits it as an ordinary
// connection (frame -> context) carrying sourceNodeName and targetNodeName;
// the frame-to-context shape cannot draw it, so a hop is resolved to its two
// nodes here.

import type { ConnectionDef, FrameDef } from '../../types/index.ts';

export interface Hop {
  /** Stable key for React lists. */
  key: string;
  frameId: string;
  /** Node ids, source first. */
  from: string;
  to: string;
}

function hopEnds(
  conn: ConnectionDef,
  frames: readonly FrameDef[],
): { frame: FrameDef; from: string; to: string } | null {
  if (!conn.sourceNodeName || !conn.targetNodeName) return null;
  const frame = frames.find((f) => f.id === conn.from);
  if (!frame) return null;
  const sourceIdx = frame.nodes.findIndex((n) => n.label === conn.sourceNodeName);
  const targetIdx = frame.nodes.findIndex((n) => n.label === conn.targetNodeName);
  // The source reads before the target: a reaction, never a cycle.
  if (sourceIdx < 0 || targetIdx < 0 || sourceIdx >= targetIdx) return null;
  return { frame, from: frame.nodes[sourceIdx].id, to: frame.nodes[targetIdx].id };
}

/** Whether a connection is a same-moment hop (both ends in the moment it belongs to). */
export function isHopConnection(conn: ConnectionDef, frames: readonly FrameDef[]): boolean {
  return hopEnds(conn, frames) !== null;
}

/** Every same-moment hop of the topology, in connection order. */
export function deriveHops(
  frames: readonly FrameDef[],
  connections: readonly ConnectionDef[],
): Hop[] {
  const hops: Hop[] = [];
  for (const conn of connections) {
    const ends = hopEnds(conn, frames);
    if (ends) {
      hops.push({
        key: `${ends.from}=>${ends.to}`,
        frameId: ends.frame.id,
        from: ends.from,
        to: ends.to,
      });
    }
  }
  return hops;
}
